import {
  UpdateAgenciesRanks,
  RemoveFromAgenciesByRankValue,
  MakeAgenciesOptions,
  FilterSelectedFromOptions,
  DataAgenciesDropdownResult,
  ReorderAgencies,
} from './DataAgenciesDropdown.types'

// Update and prevent duplicity in agency ids, in case user wants to change order of priority
export const updateAgenciesRanks = ({
  results,
  rank,
  value,
  id,
  type,
  data,
}: UpdateAgenciesRanks): DataAgenciesDropdownResult[] => {
  const remainingResults = results.filter(agency => agency.rank !== rank && agency.agency_id !== value)
  const findExistingAgency = results.find(agency => agency.agency_id === value)
  const findPreviousAgency = results.find(agency => agency.rank === rank)
  const remainingResultsUpdated = remainingResults.map(item => {
    if (findExistingAgency && item.rank < rank && item.rank > findExistingAgency?.rank) {
      return {
        ...item,
        rank: item.rank - 1,
      }
    }
    if (findExistingAgency && item.rank > rank && item.rank < findExistingAgency?.rank) {
      return {
        ...item,
        rank: item.rank + 1,
      }
    }
    return item
  })
  if (!!findExistingAgency) {
    return [
      ...remainingResultsUpdated,
      ...(findPreviousAgency
        ? [
            {
              id: findPreviousAgency.id,
              flexible: findPreviousAgency.flexible,
              allow_short_notice_booking_cancellations: false,
              agency_id: findPreviousAgency.agency_id || value,
              agency_name: findPreviousAgency.agency_name,
              rank: rank > findExistingAgency?.rank ? rank - 1 : rank + 1,
              work_location_id: data.id,
            },
          ]
        : []),
      ...(findExistingAgency
        ? [
            {
              id: findExistingAgency.id || id,
              flexible: findExistingAgency.flexible,
              allow_short_notice_booking_cancellations: false,
              agency_id: findExistingAgency.agency_id || value,
              agency_name: findExistingAgency.agency_name,
              rank: rank,
              work_location_id: data.id,
            },
          ]
        : []),
    ].sort((first, second) => first.rank - second.rank)
  }
  return [
    ...remainingResults,
    {
      id,
      agency_id: value,
      rank,
      work_location_id: data.id,
      flexible: type !== 'primary',
    },
  ].sort((first, second) => first.rank - second.rank)
}

export const removeFromAgenciesByRankValue = ({ results, rank }: RemoveFromAgenciesByRankValue) => {
  return results
    .filter(agency => agency.rank !== rank)
    .map(agency => (agency.rank > rank ? { ...agency, rank: agency.rank - 1 } : agency))
}

export const makeAgenciesOptions = ({ agencies }: MakeAgenciesOptions): { id: number; label: string }[] => {
  if (!agencies) {
    return []
  }

  const collator = new Intl.Collator('en', { sensitivity: 'base' })

  return Object.values(agencies)
    .map(agency => ({
      id: agency.id,
      label: agency.name,
    }))
    .sort((a, b) => collator.compare(a.label, b.label))
}

export const filterSelectedFromOptions = ({ options, values, selected }: FilterSelectedFromOptions) => {
  return options.filter(
    option => !values?.find(({ agency_id: agencyId }) => agencyId === option.id && option.id !== selected),
  )
}

export const isAgencyArray = (value: unknown): value is DataAgenciesDropdownResult[] => {
  return (
    Array.isArray(value) &&
    value.every(
      item =>
        typeof item === 'object' &&
        item !== null &&
        'agency_id' in item &&
        'rank' in item &&
        'work_location_id' in item,
    )
  )
}

export const filterByFlexible = (type: 'primary' | 'other') => (value: DataAgenciesDropdownResult) => {
  if (type === 'primary') {
    return !value.flexible
  } else if (type === 'other') {
    return value.flexible === true
  }
  return false
}

const byRank = (first: DataAgenciesDropdownResult, second: DataAgenciesDropdownResult) =>
  (first.rank ?? 0) - (second.rank ?? 0)

/**
 * Returns the agencies belonging to a single section (primary or other), sorted by rank. Used to
 * render and reorder rows within a section deterministically.
 */
export const getSectionAgencies = (
  results: DataAgenciesDropdownResult[],
  section: 'primary' | 'other',
): DataAgenciesDropdownResult[] => results.filter(filterByFlexible(section)).slice().sort(byRank)

/**
 * Returns the agencies as a single ordered list with primaries first (sorted by rank), followed by
 * other agencies (sorted by rank). This mirrors the contiguous rank sequence the backend expects:
 * primaries occupy ranks 1..N and other agencies occupy ranks N+1..M.
 */
export const getOrderedAgencies = (
  results: DataAgenciesDropdownResult[],
): DataAgenciesDropdownResult[] => [
  ...getSectionAgencies(results, 'primary'),
  ...getSectionAgencies(results, 'other'),
]

/**
 * Re-derives ranks (and the `flexible` flag) purely from list position for an ordered list of
 * agencies. Primaries keep `flexible: false`, other agencies `flexible: true`, and rank is the
 * 1-based index across the combined list. The `id` is cleared to null so the existing list is
 * re-created on save, matching the behaviour the backend relies on (see PTE-40718) and keeping the
 * onChange payload identical to the dropdown flow.
 */
const reindexOrderedAgencies = (
  orderedAgencies: DataAgenciesDropdownResult[],
  workLocationId: number,
): DataAgenciesDropdownResult[] =>
  orderedAgencies.map((agency, index) => ({
    ...agency,
    id: null,
    rank: index + 1,
    work_location_id: workLocationId,
  }))

/**
 * Moves a single agency (identified by its current rank) to a new position, optionally into the
 * other section. Used by both drag-and-drop and the keyboard move controls.
 *
 * - `targetSection` decides the agency's `flexible` flag after the move.
 * - `beforeRank` is the rank of the agency the moved row should be inserted *before*. Pass `null`
 *   to append to the end of the target section. The rank is resolved against the list *after* the
 *   moved row is removed, so callers never have to do off-by-one index maths.
 *
 * The whole list is returned with ranks and `flexible` re-derived from the resulting order, so the
 * caller can pass it straight to `onChange`. This replaces the index-arithmetic in
 * `updateAgenciesRanks` for the drag-and-drop experience.
 */
export const reorderAgencies = ({
  results,
  sourceRank,
  targetSection,
  beforeRank,
  data,
}: ReorderAgencies): DataAgenciesDropdownResult[] => {
  const ordered = getOrderedAgencies(results)
  const movedIndex = ordered.findIndex(agency => agency.rank === sourceRank)
  if (movedIndex === -1) {
    // Nothing matched the source rank; return a reindexed copy so callers stay consistent.
    return reindexOrderedAgencies(ordered, data.id)
  }

  const movedWithSection = { ...ordered[movedIndex], flexible: targetSection === 'other' }

  // Remove exactly the moved row by index (not by rank) so a duplicate rank could never drop more
  // than one row from the emitted payload.
  const remaining = ordered.filter((_, index) => index !== movedIndex)
  const remainingInTarget = remaining.filter(filterByFlexible(targetSection))
  const remainingInOther = remaining.filter(agency => !filterByFlexible(targetSection)(agency))

  const insertAt =
    beforeRank == null ? remainingInTarget.length : remainingInTarget.findIndex(a => a.rank === beforeRank)
  const safeIndex = insertAt === -1 ? remainingInTarget.length : insertAt
  const rebuiltTarget = [
    ...remainingInTarget.slice(0, safeIndex),
    movedWithSection,
    ...remainingInTarget.slice(safeIndex),
  ]

  // Recombine keeping the primaries-first ordering invariant regardless of which section we moved into.
  const primaries = targetSection === 'primary' ? rebuiltTarget : remainingInOther
  const others = targetSection === 'other' ? rebuiltTarget : remainingInOther

  return reindexOrderedAgencies([...primaries, ...others], data.id)
}
